import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { Bell, BellRing, Check, CheckCheck, X, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 30_000;
const SOUND_THROTTLE_MS = 5_000;

/**
 * Floating bell icon — top-right corner, fixed position.
 * - Polls /api/notifications every 30s.
 * - When unread count increases, plays a short Web Audio "ding" and triggers a ping animation.
 * - Click opens dropdown with notification list.
 * - Click item → mark read + navigate to link.
 */
export default function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pinging, setPinging] = useState(false);

  const prevUnreadRef = useRef(0);
  const lastSoundAtRef = useRef(0);
  const audioCtxRef = useRef(null);
  const initialLoadRef = useRef(true);

  // Web Audio "ding" — synthesized, no asset needed.
  const playDing = useCallback(() => {
    const now = Date.now();
    if (now - lastSoundAtRef.current < SOUND_THROTTLE_MS) return;
    lastSoundAtRef.current = now;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.18);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.42);
    } catch (e) {
      // Ignore audio failures (autoplay restrictions, etc.)
    }
  }, []);

  const triggerPing = useCallback(() => {
    setPinging(true);
    setTimeout(() => setPinging(false), 1800);
  }, []);

  const fetchNotifications = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications?limit=50`);
      const newUnread = res.data?.unread_count || 0;
      setItems(res.data?.items || []);
      setUnreadCount(newUnread);
      // Only ping/sound when unread *grows* between polls, not on first load.
      if (!initialLoadRef.current && newUnread > prevUnreadRef.current) {
        playDing();
        triggerPing();
      }
      prevUnreadRef.current = newUnread;
      initialLoadRef.current = false;
    } catch (e) {
      // Silent fail — bell stays at last known state
    } finally {
      if (!silent) setLoading(false);
    }
  }, [playDing, triggerPing]);

  // Poll loop
  useEffect(() => {
    fetchNotifications(true);
    const id = setInterval(() => fetchNotifications(true), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  // Bridge with frontend-only crm-notification events (e.g. inbound SMS) — instant ping/sound
  useEffect(() => {
    const handler = () => {
      playDing();
      triggerPing();
      // Refresh shortly after — backend may have inserted a corresponding bell notification
      setTimeout(() => fetchNotifications(true), 1500);
    };
    window.addEventListener('crm-notification', handler);
    return () => window.removeEventListener('crm-notification', handler);
  }, [playDing, triggerPing, fetchNotifications]);

  // Re-fetch when panel opens
  useEffect(() => {
    if (open) fetchNotifications(false);
  }, [open, fetchNotifications]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`);
      setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(c => Math.max(0, c - 1));
      prevUnreadRef.current = Math.max(0, prevUnreadRef.current - 1);
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      setItems(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      prevUnreadRef.current = 0;
    } catch {}
  };

  const removeNotification = async (id) => {
    try {
      await axios.delete(`${API}/notifications/${id}`);
      setItems(prev => prev.filter(n => n.id !== id));
    } catch {}
  };

  const handleItemClick = async (n) => {
    if (!n.read) await markRead(n.id);
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const badgeText = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <div className="fixed top-4 right-4 z-40">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ''}`}
            className="relative h-10 w-10 rounded-full bg-white border border-slate-200 shadow-md hover:shadow-lg hover:bg-slate-50 transition-all flex items-center justify-center"
            data-testid="notification-bell"
          >
            {unreadCount > 0 ? (
              <BellRing className={`h-5 w-5 text-indigo-600 ${pinging ? 'animate-bounce' : ''}`} />
            ) : (
              <Bell className="h-5 w-5 text-slate-600" />
            )}
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
                {badgeText}
              </span>
            )}
            {pinging && (
              <span className="absolute inset-0 rounded-full ring-2 ring-indigo-400 animate-ping" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-96 p-0" sideOffset={8}>
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="font-semibold text-slate-800">
              Notifications {unreadCount > 0 && <span className="text-xs text-slate-500 font-normal">({unreadCount} unread)</span>}
            </div>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={markAllRead} data-testid="mark-all-read">
                <CheckCheck className="h-3.5 w-3.5 mr-1" /> Mark all read
              </Button>
            )}
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Bell className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              <ul className="divide-y">
                {items.map(n => (
                  <li
                    key={n.id}
                    className={`group px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors ${
                      !n.read ? 'bg-indigo-50/40' : ''
                    }`}
                    onClick={() => handleItemClick(n)}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && (
                        <span className="mt-1.5 h-2 w-2 rounded-full bg-indigo-500 shrink-0" aria-label="unread" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!n.read ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                          {n.title}
                        </p>
                        {n.body && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.body}</p>
                        )}
                        <p className="text-[11px] text-slate-400 mt-1">
                          {n.created_at ? formatDistanceToNow(parseISO(n.created_at), { addSuffix: true }) : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                        {!n.read && (
                          <button
                            type="button"
                            className="p-1 rounded hover:bg-slate-200 text-slate-500"
                            onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                            title="Mark as read"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button
                          type="button"
                          className="p-1 rounded hover:bg-slate-200 text-slate-500"
                          onClick={(e) => { e.stopPropagation(); removeNotification(n.id); }}
                          title="Dismiss"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
